/**
 * `_shared/clay-run-status-timing` — how long Terra watches one Clay routine
 * run, as plain numbers.
 *
 * Zod-free and import-free ON PURPOSE: the server poll
 * (`launcher/clay-run-status-workflow.ts`) and the browser inspector
 * (`playbooks/dispatch-inspector-logic.ts`) both read these, and a browser
 * bundle must not drag the Clay transport or core config in to learn a number.
 *
 * # Why ~20 minutes, in two cadences
 *
 * Measured over 50 V3 runs since 2026-09-01: p50 72s, p90 712s, p95 732s,
 * max 769s (12.8 min). Every run over 5 minutes (9 of 47) was an Inflection
 * send; none passed 15 minutes. One 2.7-day outlier was a paused run, not a
 * slow one. The old 5-minute budget reported 5 of the 12 people on the 9/26
 * send as "Clay never reported back" even though all 12 were delivered.
 *
 * So: look fast while a Gong run is likely to finish (~1–2 min), then back off
 * to a slow cadence for the long Inflection tail, and stop at ~20 min — 1.5x
 * the slowest run seen. 105 looks per row instead of 61; accepted (plan
 * `2026-09-27-003`, Decisions).
 *
 *   look 1          after 3s
 *   looks 2..60     5s apart   (59 sleeps)  → ~5 min in
 *   looks 61..105   20s apart  (45 sleeps)  → ~20 min in
 */

/** A routine run needs a moment before its results endpoint answers — don't look sooner. */
export const CLAY_POLL_FIRST_DELAY_MS = 3_000;

/** Early cadence. Matches the inspector's own 5s refetch. */
export const CLAY_POLL_FAST_INTERVAL_MS = 5_000;
/** How many looks happen at the fast cadence (the first look included). */
export const CLAY_POLL_FAST_TICKS = 60;

/** Late cadence, for the long Inflection tail. */
export const CLAY_POLL_SLOW_INTERVAL_MS = 20_000;
/** How many looks happen at the slow cadence, after the fast ones. */
export const CLAY_POLL_SLOW_TICKS = 45;

/** Total looks before the poll gives up with `clay_not_found`. */
export const CLAY_POLL_MAX_TICKS = CLAY_POLL_FAST_TICKS + CLAY_POLL_SLOW_TICKS; // 105

/**
 * The sleep AFTER look `tick` (1-based), before look `tick + 1`. A pure
 * function of `tick` — the durable workflow's step sequence must be a pure
 * function of its checkpointed state, so this never reads a clock or config.
 * Looks 1..59 are followed by the fast interval; 60..104 by the slow one.
 * (Look 105 is the last; the caller does not sleep after it.)
 */
export function clayPollSleepAfterTick(tick: number): number {
  return tick < CLAY_POLL_FAST_TICKS ? CLAY_POLL_FAST_INTERVAL_MS : CLAY_POLL_SLOW_INTERVAL_MS;
}

/**
 * Wall time from the run's start to the last look: 3s + 59 × 5s + 45 × 20s =
 * 1,198,000 ms (~19.97 min). Derived, never restated.
 */
export const CLAY_POLL_BUDGET_MS =
  CLAY_POLL_FIRST_DELAY_MS
  + (CLAY_POLL_FAST_TICKS - 1) * CLAY_POLL_FAST_INTERVAL_MS
  + CLAY_POLL_SLOW_TICKS * CLAY_POLL_SLOW_INTERVAL_MS;
